import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';
import { heartbeat_hooks } from '@axo/shared/data-access/hooks';
import { useIdleTimer } from 'react-idle-timer';
import { heartbeat } from '@axo/shared/data-access/types';
import { ApplicationContext } from '../../store/context';

export function HeartbeatProvider({ children }: { children: ReactNode }) {
  const { state } = useContext(ApplicationContext);
  const currentHeartbeat = heartbeat_hooks.useHeartbeat(state.marketCountry);
  const [isIdle, setIsIdle] = useState(false);

  const timeoutInMinutes = 15;

  useIdleTimer({
    timeout: 1000 * 60 * timeoutInMinutes,
    onIdle: () => setIsIdle(true),
    onActive: () => setIsIdle(false),
    crossTab: true,
    syncTimers: 200,
  });

  useSendHeartbeats(state.marketCountry, isIdle);

  const context = {
    isActive: currentHeartbeat.data
      ? new Date(currentHeartbeat.data.DeactivatesAt) > new Date()
      : false,
  };

  return (
    <HeartbeatContext.Provider value={context}>
      {children}
    </HeartbeatContext.Provider>
  );
}

type IHeartbeatContext = { isActive: boolean };

const HB_RESEND_OFFSET_MS = 2 * 60 * 1000; // 2 minutes

export const HeartbeatContext = createContext<IHeartbeatContext>({
  isActive: false,
});

function useSendHeartbeats(marketCountry: string | undefined, idle: boolean) {
  const { mutateAsync: sendHeartbeat } = heartbeat_hooks.useSendHeartbeat(
    marketCountry ?? ''
  );

  useEffect(() => {
    if (idle || !marketCountry) return;

    let timeout: NodeJS.Timeout | null = null;
    let retryCount = 0;
    let isStopped = false;

    function stopSendingHeartbeats() {
      isStopped = true;
      if (timeout) {
        clearTimeout(timeout);
      }
    }

    function timeToNext(prev: heartbeat.HeartbeatResult) {
      const deactivateTime = new Date(prev.DeactivatesAt).valueOf();
      const millisecondsToExpiresAt = deactivateTime - new Date().valueOf();
      return Math.max(millisecondsToExpiresAt - HB_RESEND_OFFSET_MS, 0);
    }

    async function send() {
      let ms: number;
      try {
        const res = await sendHeartbeat();
        retryCount = 0;
        ms = timeToNext(res);
      } catch (e) {
        retryCount += 1;
        // Calculate exponential backoff
        const backoff = 250 * 2 ** retryCount;
        console.error(e);
        console.log(
          `Failed to send heartbeat (failure #${retryCount}). Retrying in ${backoff} ms...`
        );
        ms = backoff;
      }
      if (isStopped) return;
      timeout = setTimeout(send, ms);
    }

    function startSendingHeartbeats() {
      send();
    }

    startSendingHeartbeats();

    return () => stopSendingHeartbeats();
  }, [idle, marketCountry, sendHeartbeat]);
}
